import {
  CONFIDENCE_DISPLAY_BY_ZONE,
  CONFIDENCE_LOADING_CLASSES,
  Tabs,
} from "@modules/financialplan/constants";
import cx from "classnames";
import useStore from "@modules/financialplan/state";
import { useFinancialPlanMonteCarlo } from "@modules/financialplan/queries";
import { useCallback, useMemo } from "react";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import { Typography, Tooltip } from "@earned/core";

const FinancialPlanToolbar = () => {
  const { selectedTab, setSelectedTab } = useStore();
  const { data: monteCarloResult } = useFinancialPlanMonteCarlo({
    scenario: "recommended",
  });

  const confidenceDisplay = useMemo(
    () =>
      monteCarloResult
        ? CONFIDENCE_DISPLAY_BY_ZONE[monteCarloResult.confidenceZoneResult]
        : undefined,
    [monteCarloResult]
  );

  const confidenceColorClassName =
    confidenceDisplay?.colorClass ?? CONFIDENCE_LOADING_CLASSES.web;

  const ConfidenceIcon = useMemo(() => {
    if (!confidenceDisplay) return undefined;
    return confidenceDisplay.iconKind === "check" ? (
      <HiOutlineCheckCircle color="white" size={24} />
    ) : (
      <HiOutlineExclamationCircle color="white" size={24} />
    );
  }, [confidenceDisplay]);

  const isSelectedTab = useCallback(
    (tab: Tabs) => tab === selectedTab,
    [selectedTab]
  );

  const cookiesRequiredTooltipTitle = "Cookies Required";
  const cookiesRequiredTooltipText =
    "Sorry, you need to enable cookies to use this feature. It's not available in incognito mode.";

  // TODO: Tab component
  const Tab = ({ tab, name }: { tab: Tabs; name: string }) => (
    <Typography
      variant="body"
      color={isSelectedTab(tab) ? "primary" : "secondary"}
      weight={isSelectedTab(tab) ? "medium" : "normal"}
    >
      <button
        className={cx(
          "hover:text-green-1",
          isSelectedTab(tab) ? "tracking-tight" : ""
        )}
        onClick={() => setSelectedTab(tab)}
      >
        {name}
      </button>
    </Typography>
  );

  return (
    <div className="flex h-full flex-col justify-between px-10 py-6 md:flex-row md:items-center">
      <div className="flex flex-1 flex-row items-center space-x-6">
        <Tab tab={Tabs.PlanOverview} name="Plan Overview" />
        {!navigator.cookieEnabled ? (
          <Tooltip
            title={cookiesRequiredTooltipTitle}
            description={cookiesRequiredTooltipText}
          >
            <Tab tab={Tabs.PlayZone} name="Play with plan" />
          </Tooltip>
        ) : (
          <Tab tab={Tabs.PlayZone} name="Play with plan" />
        )}
        {!navigator.cookieEnabled ? (
          <Tooltip
            title={cookiesRequiredTooltipTitle}
            description={cookiesRequiredTooltipText}
          >
            <Tab tab={Tabs.Fears} name="Test your fears" />
          </Tooltip>
        ) : (
          <Tab tab={Tabs.Fears} name="Test your fears" />
        )}
      </div>
      {/* TODO: Status Chip for Financial Plan */}
      <div className="mt-6 flex-none md:mt-0">
        <div
          className={
            "flex flex-row items-center justify-center gap-2 rounded-full px-4 py-3 text-body font-medium text-white transition-all " +
            confidenceColorClassName
          }
        >
          <span>{ConfidenceIcon}</span>
          {confidenceDisplay && <span>{confidenceDisplay.text}</span>}
        </div>
      </div>
    </div>
  );
};

export default FinancialPlanToolbar;
